import PlatformTypeBadge from '@boComponents/PlatformTypeBadge';
import useGuardedTabs from '@boHooks/useGuardedTabs';
import ContextTab from '@boViews/shared/ContextTab';
import { faArrowLeft, faLockKeyhole } from '@fortawesome/pro-regular-svg-icons';
import Accordion from '@sharedComponents/Accordion';
import Button from '@sharedComponents/Button';
import FlexBox from '@sharedComponents/FlexBox';
import HtmlContent from '@sharedComponents/HtmlContent';
import InformationMessage from '@sharedComponents/InformationMessage';
import TabBar from '@sharedComponents/TabBar';
import Tag from '@sharedComponents/Tag';
import Typography from '@sharedComponents/Typography';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import InformationTab from './InformationTab';

const INFORMATION_TAB = 0;
const CONTEXT_TAB = 1;

const PlatformDetail = ({
    initialPlatform = null,
    context = null,
    contextLoading = false,
    contextError = null,
    clientContext = null,
    onBack = () => {},
    onSave = () => {},
    onSaveContext = async () => null,
    onContextDirtyChange = () => {},
    onOpenClient = () => {},
} = {}) => {
    const { t } = useTranslation('bo');
    const [platform, setPlatform] = useState(initialPlatform);
    const { activeTab, selectTab, reportDirty } = useGuardedTabs(CONTEXT_TAB, onContextDirtyChange, INFORMATION_TAB);

    if (!platform) return null;

    // The platforms of an inactive client are read-only (the API refuses their modification).
    const readOnly = platform.client?.active === false;

    const tabs = [
        { label: t('Information'), selected: activeTab === INFORMATION_TAB },
        { label: t('Context'), selected: activeTab === CONTEXT_TAB },
    ];

    // Only the platform returned by a successful save is displayed; a failed save leaves the form as typed.
    const handleSave = async (data) => {
        const saved = await onSave(data);
        if (saved) setPlatform(saved);
    };

    return (
        <FlexBox flexDirection="column" gap="m" additionnalStyles={{ padding: 'var(--space-l) var(--space-xl)' }}>
            <Button ghost noPadding icon={faArrowLeft} label={t('Back to client')} action={onBack} style={{ alignSelf: 'flex-start' }} />

            <FlexBox justifyContent="space-between" alignItems="center">
                <FlexBox flexDirection="column" gap="xxs">
                    <FlexBox gap="s" alignItems="center">
                        <Typography fontSize="xl" fontWeight="bold" color="branded-primary" seo={1}>
                            {platform.name}
                        </Typography>
                        <PlatformTypeBadge type={platform.type} />
                    </FlexBox>
                    <Typography color="neutral-secondary">{platform.url}</Typography>
                </FlexBox>
                <Tag color={platform.active ? 'success' : 'disabled'}>{platform.active ? t('Active') : t('Inactive')}</Tag>
            </FlexBox>

            <FlexBox additionnalStyles={{ borderBottom: '1px solid var(--background-color-neutral-secondary)' }}>
                <TabBar tabs={tabs} onClick={selectTab} />
            </FlexBox>

            {/* Remounted after each save so the form restarts from the server-normalized values (e.g. URL). */}
            {activeTab === INFORMATION_TAB && (
                <InformationTab key={platform.updatedAt ?? platform.uuid} platform={platform} onSave={handleSave} />
            )}

            {activeTab === CONTEXT_TAB && (
                <FlexBox flexDirection="column" gap="m">
                    {readOnly && (
                        <InformationMessage icon={faLockKeyhole} text={t('The client is inactive: this platform is read-only.')} />
                    )}
                    <ContextTab
                        context={context}
                        loading={contextLoading}
                        error={contextError}
                        readOnly={readOnly}
                        emptyTitle={t('No context for this platform')}
                        emptyDescription={t(
                            'Note what the team should know about this platform: contacts, uses, expectations, points of attention.',
                        )}
                        onSave={onSaveContext}
                        onDirtyChange={reportDirty}
                    />
                    {clientContext?.html && (
                        <Accordion title={t('Context of the client {{name}}', { name: platform.client?.name })}>
                            <FlexBox flexDirection="column" gap="s">
                                <HtmlContent html={clientContext.html} />
                                <Button
                                    ghost
                                    noPadding
                                    label={t('Open the client page')}
                                    action={onOpenClient}
                                    style={{ alignSelf: 'flex-start' }}
                                />
                            </FlexBox>
                        </Accordion>
                    )}
                </FlexBox>
            )}
        </FlexBox>
    );
};

export default PlatformDetail;
